import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'
import { MarketingParametersDto } from '@mos/api/game/dto-client'
import {
	DailyCreditNotification,
	MemoryChapterEndedNotification,
	MemoryChapterStartedNotification,
	NotificationType,
	PlayerRegistrationNotification,
	StoryChapterEndedNotification,
	StoryChapterStartedNotification,
} from '@mos/api/notification/dto-client'
import { Player } from '@mos/api/player/dto-client'
import { AcquisitionVisitDto } from '@mos/api/tracking/dto'
import { AppConfigService } from '@mos/client/shared/app-config'
import { isServer } from '@mos/client/shared/utils'
import { ApiNotificationService } from '@mos/client/web/api-notification/lib/api-notification.service'
import { AdRewardService } from '@mos/client/web/shop/data-access/services/ads/ad-reward.service'
import { ShopService } from '@mos/client/web/shop/data-access/services/shop.service'
import { AcquisitionRepository, AuthRepository } from '@mos/shared/auth'
import { GoogleTagManagerService } from 'angular-google-tag-manager'
import { distinctUntilChanged, filter, map, of, pairwise, Subject, take } from 'rxjs'
import { TrackingRepository } from './tracking.repository'

interface EventLayer {
	event: string
	eventId?: string
	playerId?: number
}

const TAGS_READ_URL_DELAY_MS = 3000

@Injectable({ providedIn: 'root' })
export class TrackingService {
	private readonly authRepository = inject(AuthRepository)
	private readonly trackingRepository = inject(TrackingRepository)
	private readonly gtmService = inject(GoogleTagManagerService)
	private readonly apiNotificationService = inject(ApiNotificationService)
	private readonly router = inject(Router)
	private readonly http = inject(HttpClient)
	private readonly shopService = inject(ShopService)
	private readonly adRewardService = inject(AdRewardService)
	private readonly appConfigService = inject(AppConfigService)
	private readonly acquisitionRepository = inject(AcquisitionRepository)

	private loadingTrackingParameters = false
	private trackingParametersLoaded$ = new Subject<void>()

	constructor() {
		if (isServer) {
			return
		}

		this.authRepository.player$.pipe(distinctUntilChanged((a, b) => a?.id === b?.id)).subscribe((player) => {
			if (!player) {
				this.trackingRepository.logout()
				this.loadingTrackingParameters = false
				return
			}
			this.loadTrackingParameters()
		})

		this.shopService.newCheckout$.subscribe((checkout) => {
			this.trackingRepository.setPendingCheckout(checkout)
		})

		this.trackLandingVisit()
		this.trackRegistration()
		this.trackFirstLogin()
		this.trackPageView()
		this.trackChapterStart()
		this.trackChapterEnd()
		this.trackMemoryStart()
		this.trackMemoryEnd()
		this.trackTutorialStart()
		this.trackTutorialEnd()
		this.trackAdRewardStart()
		this.trackAdRewardCredit()
	}

	private loadTrackingParameters() {
		this.loadingTrackingParameters = true
		this.http.get<MarketingParametersDto>('@api/marketing').subscribe((params) => {
			this.trackingRepository.set(true, params.isPremium, {
				chapterNumber: params.currentChapterNumber,
				memoryNumber: params.currentMemoryNumber,
				isInReplay: params.isInReplay,
			})

			this.loadingTrackingParameters = false
			this.trackingParametersLoaded$.next()
		})
	}

	private getTrackingParameters() {
		if (!this.loadingTrackingParameters) {
			return of(this.trackingRepository.state)
		}

		return this.trackingParametersLoaded$.pipe(
			take(1),
			map(() => this.trackingRepository.state),
		)
	}

	// staging/preprod currently share the same GTM container as prod (hardcoded ID in
	// app.config.ts), so never boot GTM outside of real production - otherwise QA/staff
	// activity pollutes the live Meta Pixel/Google Ads data. `environment` is the runtime
	// string served via /env.json ('staging'/'preprod'/'prod' on web, 'development'/
	// 'production' on mobile - see AppConfigService), not the Angular build-time flag.
	private isProductionEnvironment(): boolean {
		const environment = this.appConfigService.getEnv().environment
		return environment === 'prod' || environment === 'production'
	}

	/** Resolves once pushed (at once when there is no GTM), even if GTM failed to load */
	private pushLayer(layer: EventLayer): Promise<void> {
		if (!this.isProductionEnvironment()) {
			return Promise.resolve()
		}

		return new Promise((resolve) => {
			this.getTrackingParameters().subscribe((params) => {
				this.gtmService
					.pushTag({
						...layer,
						// Each game now has its own GTM container, so this is a reporting dimension
						// rather than the routing key tags used to filter on.
						game: this.appConfigService.getDisplayName(),
						is_connected: params.isConnected,
						is_premium: params.isPremium,
						chapter_number: params.story?.chapterNumber,
						memory_number: params.story?.memoryNumber,
						replay: params.story?.isInReplay,
					})
					.then(
						() => resolve(),
						() => resolve(),
					)
			})
		})
	}

	trackRegistration() {
		this.apiNotificationService
			.on<PlayerRegistrationNotification>(NotificationType.PLAYER_REGISTRATION)
			.subscribe((notification) => {
				this.pushLayer({
					event: 'sign_up',
					eventId: `sign_up_${notification.playerId}`,
					playerId: notification.playerId,
				})
			})
	}

	private trackAdRewardStart() {
		this.adRewardService.adrewardStarted$.pipe(takeUntilDestroyed()).subscribe(() => {
			this.pushLayer({
				event: 'adreward_start',
				playerId: this.authRepository.player?.id,
			})
		})
	}

	private trackAdRewardCredit() {
		this.adRewardService.adrewardCredited$.pipe(takeUntilDestroyed()).subscribe(() => {
			this.pushLayer({
				event: 'adreward_credit',
				playerId: this.authRepository.player?.id,
			})
		})
	}

	trackPurchase() {
		const pendingCheckout = this.trackingRepository.pendingCheckout()
		if (!pendingCheckout) {
			return
		}

		this.pushLayer({
			event: 'purchase',
			eventId: `purchase_${pendingCheckout.id}`,
			transaction_id: pendingCheckout.id,
			currency: pendingCheckout.currency.toUpperCase(),
			value: pendingCheckout.value,
			payment_method: 'stripe',
			new_customer: pendingCheckout.newCustomer,
			tax: 0,
			item_id: [
				{
					item_id: pendingCheckout.offerId,
					item_name: pendingCheckout.offerName,
				},
			],
			playerId: pendingCheckout.playerId,
		} as EventLayer)

		this.trackingRepository.setPremium()
		this.trackingRepository.removePendingCheckout()
	}

	private trackChapterStart() {
		this.apiNotificationService
			.on<StoryChapterStartedNotification>(NotificationType.STORY_CHAPTER_STARTED)
			.subscribe((notification) => {
				this.trackingRepository.updateChapter(notification.chapterNumber, !notification.isFirstTime)
				this.pushLayer({
					event: 'chapter_start',
					playerId: this.authRepository.player?.id ?? undefined,
				})
			})
	}

	private trackChapterEnd() {
		this.apiNotificationService
			.on<StoryChapterEndedNotification>(NotificationType.STORY_CHAPTER_ENDED)
			.subscribe((notification) => {
				this.trackingRepository.updateChapter(notification.chapterNumber, !notification.isFirstTime)
				this.pushLayer({
					event: 'chapter_end',
					playerId: this.authRepository.player?.id ?? undefined,
				})
			})
	}

	private trackMemoryStart() {
		this.apiNotificationService
			.on<MemoryChapterStartedNotification>(NotificationType.MEMORY_CHAPTER_STARTED)
			.subscribe((notification) => {
				this.trackingRepository.updateMemory(notification.chapterNumber)
				this.pushLayer({
					event: 'memory_start',
					playerId: this.authRepository.player?.id ?? undefined,
				})
			})
	}

	private trackMemoryEnd() {
		this.apiNotificationService
			.on<MemoryChapterEndedNotification>(NotificationType.MEMORY_CHAPTER_ENDED)
			.subscribe((notification) => {
				this.trackingRepository.updateMemory(notification.chapterNumber)
				this.pushLayer({
					event: 'memory_end',
					playerId: this.authRepository.player?.id ?? undefined,
				})
			})
	}

	private trackTutorialStart() {
		this.apiNotificationService
			.on<StoryChapterStartedNotification>(NotificationType.STORY_CHAPTER_STARTED)
			.pipe(filter((notification) => notification.chapterNumber === 1 && notification.isFirstTime))
			.subscribe((notification) => {
				this.pushLayer({
					event: 'tutorial_start',
					eventId: `tutorial_start_${notification.playerId}`,
					playerId: notification.playerId,
				})
			})
	}

	private trackTutorialEnd() {
		this.authRepository.player$
			.pipe(
				filter((player): player is Player => player !== null),
				distinctUntilChanged(
					(a, b) =>
						a.id === b.id && a.currencies.bankFreeTicketsCredited === b.currencies.bankFreeTicketsCredited,
				),
				pairwise(),
				filter(
					([a, b]) =>
						a.id === b.id &&
						a.currencies.bankFreeTicketsCredited === false &&
						b.currencies.bankFreeTicketsCredited === true,
				),
			)
			.subscribe(([player]) => {
				this.pushLayer({
					event: 'tutorial_end',
					eventId: `tutorial_end_${player.id}`,
					playerId: player.id,
				})
			})
	}

	private trackFirstLogin() {
		this.apiNotificationService
			.on<DailyCreditNotification>(NotificationType.DAILY_CREDIT)
			.subscribe((notification) => {
				this.pushLayer({
					event: 'login',
					eventId: `login_${notification.playerId}_${new Date().toISOString()}`,
					playerId: notification.playerId,
				})
			})
	}

	private trackPageView() {
		let isLanding = true
		this.router.events
			.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
			.subscribe((navigationEnd) => {
				const pushed = this.pushLayer({
					event: 'page_view',
					pageName: navigationEnd.url,
				} as EventLayer)

				if (isLanding) {
					isLanding = false
					// The landing's utm_*/gclid/fbclid stay in the address bar until GTM's tags, firing once it is
					// loaded, have read them for their own attribution (GA4, Google Ads, Meta)
					pushed.then(() =>
						setTimeout(() => this.acquisitionRepository.removeParamsFromUrl(), TAGS_READ_URL_DELAY_MS),
					)
				}
			})
	}

	// Anonymous count of landings with UTM params (the "visits" of the admin acquisition stats), sent once
	// the API url is known.
	private trackLandingVisit() {
		const landing = this.acquisitionRepository.landing
		if (!landing || (!landing.utmSource && !landing.utmMedium && !landing.utmCampaign)) {
			return
		}

		const visit: AcquisitionVisitDto = {
			utmSource: landing.utmSource,
			utmMedium: landing.utmMedium,
			utmCampaign: landing.utmCampaign,
			utmContent: landing.utmContent,
		}
		this.appConfigService.init$
			.pipe(filter(Boolean), take(1))
			.subscribe(() => this.http.post('@api/tracking/visit', visit).subscribe({ error: () => undefined }))
	}
}
